import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as LinkIcon, Calendar, Check, Download, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Input } from '@/components/shared/ui';
import { ClassItem } from '../../data/classes';
import { classInviteService } from '../../services/classInviteService';
import { ClassInvite } from '../../types/class';
import { BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT } from '@/utils/bulkCreateGuestValidity';
import { formatLocaleDateShort } from '@/lib/localeFormat';

interface ClassInviteModalProps {
  classData: ClassItem;
}

const INVITE_STORAGE_PREFIX = 'medicrew:classInvite:v1';

function inviteStorageKey(classId: string): string {
  return `${INVITE_STORAGE_PREFIX}:${classId}`;
}

function readCachedInvite(classId: string): ClassInvite | null {
  try {
    const raw = localStorage.getItem(inviteStorageKey(classId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { invite?: ClassInvite };
    const inv = parsed.invite;
    if (!inv || String(inv.classId) !== String(classId)) return null;
    if (inv.status !== 'active') return null;
    // token이 없는 유효하지 않은 초대는 캐시 제거
    if (!inv.token || inv.token.trim() === '') {
      localStorage.removeItem(inviteStorageKey(classId));
      return null;
    }
    const expMs = new Date(inv.expiresAt).getTime();
    if (!Number.isFinite(expMs) || expMs <= Date.now()) {
      localStorage.removeItem(inviteStorageKey(classId));
      return null;
    }
    return inv;
  } catch {
    return null;
  }
}

function writeCachedInvite(classId: string, invite: ClassInvite): void {
  try {
    localStorage.setItem(inviteStorageKey(classId), JSON.stringify({ invite }));
  } catch {}
}

function clearCachedInvite(classId: string): void {
  localStorage.removeItem(inviteStorageKey(classId));
}

const expirationOptionKeys: Record<number, string> = { 7: 'expiration7', 14: 'expiration14', 30: 'expiration30', 60: 'expiration60', 90: 'expiration90', 0: 'expirationUnlimited' };

export default function ClassInviteModal({ classData }: ClassInviteModalProps): React.ReactElement {
  const { t, i18n } = useTranslation('class');
  const [expirationDays, setExpirationDays] = useState<number>(BULK_CREATE_GUEST_VALIDITY_DAYS_DEFAULT);
  const [isGenerating, setIsGenerating] = useState(false);
  const [invite, setInvite] = useState<ClassInvite | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const classId = classData.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const active = await classInviteService.loadActiveInvite(classId);
      if (cancelled) return;
      if (active && active.token && active.token.trim() !== '') {
        setInvite(active);
        writeCachedInvite(classId, active);
        return;
      }
      const cached = readCachedInvite(classId);
      if (!cancelled && cached && cached.token && cached.token.trim() !== '') {
        setInvite(cached);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId]);

  const expirationOptions = [
    { value: 7 },
    { value: 14 },
    { value: 30 },
    { value: 60 },
    { value: 90 },
    { value: 0 },
  ];

  const handleGenerateInvite = async () => {
    setIsGenerating(true);
    try {
      const newInvite = await classInviteService.createInvite({
        classId,
        expirationDays: expirationDays,
      });
      setInvite(newInvite);
      writeCachedInvite(classId, newInvite);
    } catch {
      alert(t('modals.inviteCreateFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  const inviteUrl = invite ? `${window.location.origin}/class/invite/${invite.token}` : '';
  /** API·캐시에 inviteCode가 없을 때 토큰으로 대체 (undefined.length 방지) */
  const displayInviteCode = invite
    ? String(invite.inviteCode ?? invite.token ?? '').trim()
    : '';

  const copyToClipboard = (text: string, type: 'link' | 'code') => {
    navigator.clipboard.writeText(text);
    if (type === 'link') {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('invite-qrcode');
    if (!svg || !(svg instanceof SVGSVGElement)) {
      alert(t('modals.qrDownloadFailed'));
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `invite-qr-${classId}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.onerror = () => {
      alert(t('modals.qrDownloadFailed'));
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleRegenerate = () => {
    clearCachedInvite(classId);
    setInvite(null);
  };

  return (
    <div className="space-y-6 py-2">
      {!invite ? (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="text-blue-800 font-semibold text-sm mb-1 flex items-center gap-2">
              <LinkIcon size={16} /> {t('modals.inviteCreateNew')}
            </h4>
            <p className="text-blue-700 text-sm">
              {t('modals.inviteCreateDescription')}
            </p>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Calendar size={14} /> {t('modals.inviteValidityLabel')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {expirationOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setExpirationDays(option.value)}
                  className={`py-2 px-3 text-sm rounded-md border transition-all ${
                    expirationDays === option.value
                      ? 'bg-blue-600 border-blue-600 text-white font-medium shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {t(`modals.${expirationOptionKeys[option.value as keyof typeof expirationOptionKeys]}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {expirationDays === 0
                ? t('modals.inviteExpirationUnlimited')
                : t('modals.inviteExpirationDays', { days: expirationDays })}
            </p>
          </div>

          <Button
            variant="primary"
            className="w-full h-12 text-base font-semibold"
            onClick={handleGenerateInvite}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <RefreshCw size={18} className="mr-2 animate-spin" />
                {t('modals.generating')}
              </>
            ) : (
              t('modals.inviteGenerateButton')
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-100">
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4 border border-gray-200 flex items-center justify-center">
              <QRCodeSVG
                id="invite-qrcode"
                value={inviteUrl}
                size={176}
                level="M"
                includeMargin
                aria-label={t('modals.inviteLinkLabel')}
              />
            </div>
            <Button variant="outline" size="sm" onClick={downloadQRCode} className="text-xs h-8" type="button">
              <Download size={14} className="mr-1.5" /> {t('modals.qrDownload')}
            </Button>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-800 ml-1">{t('modals.inviteLinkLabel')}</label>
              <div className="flex gap-2">
                <Input
                  value={inviteUrl}
                  readOnly
                  className="bg-gray-50 text-gray-600 text-sm"
                />
                <Button
                  variant={copiedLink ? 'secondary' : 'primary'}
                  className="shrink-0 w-24"
                  type="button"
                  onClick={() => copyToClipboard(inviteUrl, 'link')}
                >
                  {copiedLink ? <Check size={18} /> : t('modals.copy')}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-800 ml-1">{t('modals.inviteCodeLabel')}</label>
              <div className="flex gap-2">
                <div className="flex-1 bg-white border-2 border-dashed border-blue-200 rounded-lg flex items-center justify-center py-2 px-3 min-h-[3rem]">
                  <span
                    className={
                      displayInviteCode.length > 12
                        ? 'text-sm sm:text-base font-mono font-bold text-blue-700 text-center break-all leading-snug'
                        : 'text-2xl font-black tracking-[0.2em] text-blue-600'
                    }
                  >
                    {displayInviteCode || '—'}
                  </span>
                </div>
                <Button
                  variant={copiedCode ? 'secondary' : 'primary'}
                  className="shrink-0 w-24"
                  type="button"
                  onClick={() => copyToClipboard(displayInviteCode, 'code')}
                  disabled={!displayInviteCode}
                >
                  {copiedCode ? <Check size={18} /> : t('modals.copy')}
                </Button>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">{t('modals.expiresAt')}</span>
              <span className="text-sm font-medium text-gray-700">
                {invite.expiresAt != null && String(invite.expiresAt).trim() !== ''
                  ? new Date(invite.expiresAt).getFullYear() >= 2100
                    ? t('modals.inviteExpirationUnlimited')
                    : formatLocaleDateShort(invite.expiresAt, i18n.language)
                  : t('modals.inviteExpirationUnlimited')}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={handleRegenerate}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              {t('modals.regenerate')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
